import {AfterViewInit, OnDestroy, Component, ElementRef, Input, OnInit, ViewChild, ChangeDetectorRef} from '@angular/core';
import _ from 'lodash';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'ui-action-list',
  templateUrl: './ui-action-list.component.html'
})
export class UIActionListComponent implements AfterViewInit, OnInit, OnDestroy {
  /* A ViewChild reference to the action list in the DOM */
  @ViewChild('actionList') actionList: ElementRef<HTMLDivElement>;

  /* The element that will trigger the action list to open and close */
  private TRIGGER_REF: HTMLElement;

  /* The index of which the navigation is at */
  public navIndex: number;

  /* The element that will trigger the action list to open and close */
  @Input() set triggerRef(val: HTMLElement) {
    this.TRIGGER_REF = val;
    this.linkToTriggerRef();
  }

  /* The element that will trigger the action list to open and close */
  get triggerRef(): HTMLElement {
    const actionList = this.actionList?.nativeElement;
    return this.TRIGGER_REF ?? (
      actionList?.parentElement?.previousElementSibling ?? actionList?.parentElement?.parentElement
    ) as HTMLElement;
  }

  /* Returns weather the trigger element is focused to determine the display of the action list */
  get isTriggerFocused() {
    this.handleScroll();
    const is = document.activeElement === this.triggerRef;
    if (!is && !!this.actionList?.nativeElement) { this.resetNavigation(); }
    return is;
  }

  /* Position  properties for the action list */
  actionListPosition: {top: number, left: number} = {
    top: 0,
    left: 0
  };

  /* Callback to reposition the action list for when the page is scrolled */
  handleScroll = _.throttle(() => {
    this.actionListPosition = {
      top: this.getTopPosition(),
      left: this.getLeftPosition()
    };
    this.changeDetectorRef.markForCheck();
  }, 300, {leading: true, trailing: true});

  constructor(
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.listenForScrolling();
    this.linkToTriggerRef();
    this.removeAllButUIActions();
    this.divideIntoGroups();
  }

  listenForScrolling() {
    this.handleScroll();
    document.addEventListener('scroll', this.handleScroll);
  }

  linkToTriggerRef() {
    const handleKeyDown = (event: KeyboardEvent): void => {
    event.preventDefault();
    switch (event.key) {
      case 'ArrowUp':
        this.navigateUp();
        break;
      case 'ArrowDown':
        this.navigateDown();
        break;
      case 'Enter':
        this.clickNavigatedAction();
        break;
      default:
        break;
    }
    this.changeDetectorRef.markForCheck();
  };

    this.triggerRef?.setAttribute('tabindex', '0');
    this.triggerRef?.addEventListener('keydown', handleKeyDown);
  }

  removeAllButUIActions(): void {
      Array.from(this.actionList.nativeElement.childNodes).forEach((child: HTMLElement) => {
          if (!child.tagName || child.tagName !== 'UI-ACTION') {
              child.remove();
          }
      });
  }

  divideIntoGroups(): void {
      const allActions = Array.from(this.actionList.nativeElement.children);
      const allGroups = [...new Set(allActions.map((action: HTMLElement) => (action.firstChild as HTMLElement).dataset['group']))];
      const groupDivider = (groupName: string) => {
          // if (!groupName)
          //     return;
          const divider = document.createElement('div');
          divider.classList.add('≈px-3.5', `${groupName ? '≈py-2' : null}`, 'md:≈px-5.5', `${groupName ? 'md:≈py-4' : null}`,
              '≈border-b', '≈border-divider', '≈font-medium');
          divider.innerText = groupName ?? '';
          return divider;
      };
      let groupedActions: Element[] = [];
      let actionsWithoutGroup: Element[] = [];

      for (const group of allGroups) {
          if (!group) {
              actionsWithoutGroup = allActions.filter((action: HTMLElement) =>
                (action.firstChild as HTMLElement).dataset.group === group);
              continue;
          }
          groupedActions = groupedActions.concat(
            [groupDivider(group),
              ...allActions.filter((action: HTMLElement) => (action.firstChild as HTMLElement).dataset['group'] == group)]
          );
      }
      if (actionsWithoutGroup.length) {
          groupedActions = groupedActions.concat(groupedActions.length
            ? [groupDivider(''), ...actionsWithoutGroup]
            : actionsWithoutGroup);
      }

      this.actionList.nativeElement.innerHTML = '';
      this.actionList.nativeElement.append(...groupedActions)
  }

  getLeftPosition(): number {
    if (!this.triggerRef || !this.actionList) {
        return;
    }
    if (this.triggerRef.getBoundingClientRect().left < 0) { return 0; }
    else if (this.triggerRef.getBoundingClientRect().right > window.innerWidth - 10) {
      return (window.innerWidth - this.actionList.nativeElement.getBoundingClientRect().width) - 10;
    }
    const isNearRightEdge = (window.innerWidth - this.triggerRef.getBoundingClientRect().left)
        <= this.actionList.nativeElement.scrollWidth;
    return isNearRightEdge
        ? this.triggerRef.getBoundingClientRect().right - this.actionList.nativeElement.scrollWidth
        : this.triggerRef.getBoundingClientRect().left;
  }

  getTopPosition(): number {
    if (!this.triggerRef || !this.actionList) {
        return;
    }
    if (this.triggerRef.getBoundingClientRect().top < 0) { return 0; }
    else if (this.triggerRef.getBoundingClientRect().bottom > window.innerHeight - 10) {
      return (window.innerHeight - this.actionList.nativeElement.getBoundingClientRect().height) - 10;
    }
    const isNearBottomEdge = (window.innerHeight - this.triggerRef.getBoundingClientRect().top)
        <= this.actionList.nativeElement.scrollHeight;
    return isNearBottomEdge
        ? (this.triggerRef.getBoundingClientRect().top - this.actionList.nativeElement.scrollHeight) - 5
        : this.triggerRef.getBoundingClientRect().bottom + 5;
  }

  get allUIActions(): Element[] {
      return Array.from(this.actionList.nativeElement.children).filter((child: HTMLElement) => child.tagName === 'UI-ACTION');
  }

  resetNavigation(): void {
      this.allUIActions.forEach(action => action.firstChild['navigating'] = false);
      this.navIndex = undefined;
  }

  highlightNavigatedAction(): void {
      if (!this.allUIActions) { return; }
      this.allUIActions.forEach(action => action.firstChild['navigating'] = false);
      this.allUIActions[this.navIndex].firstChild['navigating'] = true;
  }

  navigateDown(): void {
      if (!this.isTriggerFocused || !this.allUIActions.length) { return; }
      if (!this.allUIActions[this.navIndex + 1]) {
          this.navIndex = 0;
      }
      else {
          this.navIndex++;
      }
      this.highlightNavigatedAction();
  }

  navigateUp(): void {
      if (!this.isTriggerFocused || !this.allUIActions.length) { return; }
      if (!this.allUIActions[this.navIndex - 1]) {
          this.navIndex = this.allUIActions.length - 1;
      }
      else {
          this.navIndex--;
      }
      this.highlightNavigatedAction();
  }

  clickNavigatedAction(): void {
      const clickEvent = new MouseEvent('click');
      !!this.allUIActions && this.allUIActions[this.navIndex].firstChild.dispatchEvent(clickEvent);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.handleScroll);
  }
}
